import React, { useState } from "react";
import classNames from "classnames";
import {
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  EyeIcon,
  EyeSlashIcon,
  PlusIcon,
  ShoppingCartIcon,
  GlobeAltIcon,
  CheckCircleIcon,
  XMarkIcon,
  FunnelIcon,
  MapPinIcon,
  TruckIcon,
  BuildingStorefrontIcon,
  ChatBubbleLeftRightIcon,
  ArrowTopRightOnSquareIcon,
  DocumentArrowDownIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { money, toFloat } from "./envois-shared";

export interface EcommerceOrderItem {
  id: string;
  orderRef: string;
  platform: "Amazon France" | "Amazon USA" | "AliExpress" | "Shein" | "Alibaba B2B" | "Zara / Fashion" | "Boutique Pro";
  vendorOrderNo: string;
  clientName: string;
  clientPhone: string;
  clientDestination: string;
  villeFinale: "Garoua" | "Douala" | "Yaoundé" | "Maroua" | "Bafoussam";
  hubTransit: "Hub Paris Roissy (CDG)" | "Hub Dubaï Cargo" | "Hub Guangzhou Chine" | "Hub Bruxelles";
  trackingVendor: string;
  itemsDescription: string;
  poidsKg: number;
  valeurOrigine: number; // en Euros ou Dollars
  monnaie: "EUR" | "USD";
  fretInternational: number; // FCFA
  douaneTaxes: number; // FCFA
  acheminementFinal: number; // FCFA
  totalClient: number; // FCFA
  paymentStatus: "Payé à 100%" | "Acompte 50%" | "Solde à livraison" | "Devis en attente";
  logisticStatus:
    | "Commandé chez marchand"
    | "Réceptionné Hub Roissy"
    | "En vol / fret maritime"
    | "Dédouanement Cameroun"
    | "Acheminement Garoua"
    | "Arrivé agence locale"
    | "Livré au client";
  orderDate: string;
  eta: string;
  notes: string;
}

const SEED_ECOMMERCE_ORDERS: EcommerceOrderItem[] = [
  {
    id: "ec1",
    orderRef: "ECM-2609-AMZ-0091",
    platform: "Amazon France",
    vendorOrderNo: "408-7291823-19283",
    clientName: "Ahmadou Bello",
    clientPhone: "+237 699 88 12 34",
    clientDestination: "Garoua, Quartier Marouare face Grande Mosquée",
    villeFinale: "Garoua",
    hubTransit: "Hub Paris Roissy (CDG)",
    trackingVendor: "FR-COL-9928114",
    itemsDescription: "Apple MacBook Air 13'' M2 + Souris Magic Mouse",
    poidsKg: 2.6,
    valeurOrigine: 1199,
    monnaie: "EUR",
    fretInternational: 38000,
    douaneTaxes: 45000,
    acheminementFinal: 6000,
    totalClient: 89000,
    paymentStatus: "Payé à 100%",
    logisticStatus: "Acheminement Garoua",
    orderDate: "20/09/2026",
    eta: "Demain 14:00 (Agence Garoua)",
    notes: "Colis sous assurance haute valeur. Client averti par WhatsApp.",
  },
  {
    id: "ec2",
    orderRef: "ECM-2609-ALI-0104",
    platform: "AliExpress",
    vendorOrderNo: "ALI-889102934",
    clientName: "Mme Fotso Mireille",
    clientPhone: "+237 677 33 44 55",
    clientDestination: "Douala, Bonapriso Rue des Palmiers",
    villeFinale: "Douala",
    hubTransit: "Hub Guangzhou Chine",
    trackingVendor: "CN-EMS-4410982",
    itemsDescription: "Lot 15 robes de soirée & accessoires mode",
    poidsKg: 5.8,
    valeurOrigine: 380,
    monnaie: "USD",
    fretInternational: 32000,
    douaneTaxes: 18000,
    acheminementFinal: 2500,
    totalClient: 52500,
    paymentStatus: "Acompte 50%",
    logisticStatus: "Dédouanement Cameroun",
    orderDate: "12/09/2026",
    eta: "29/09/2026",
    notes: "Dédouanement aéroport Douala en cours.",
  },
  {
    id: "ec3",
    orderRef: "ECM-2609-AMZ-0108",
    platform: "Amazon France",
    vendorOrderNo: "408-1192845-66712",
    clientName: "Dr. Ousmanou Bouba",
    clientPhone: "+237 690 11 22 99",
    clientDestination: "Garoua, Hôpital Régional Garoua",
    villeFinale: "Garoua",
    hubTransit: "Hub Paris Roissy (CDG)",
    trackingVendor: "FR-DHL-7718290",
    itemsDescription: "Stéthoscope électronique 3M Littmann + Tensiomètres",
    poidsKg: 1.8,
    valeurOrigine: 480,
    monnaie: "EUR",
    fretInternational: 26000,
    douaneTaxes: 20000,
    acheminementFinal: 5000,
    totalClient: 51000,
    paymentStatus: "Payé à 100%",
    logisticStatus: "En vol / fret maritime",
    orderDate: "22/09/2026",
    eta: "01/10/2026",
    notes: "Matériel médical prioritaire. Vol AF 947.",
  },
  {
    id: "ec4",
    orderRef: "ECM-2609-SHE-0112",
    platform: "Shein",
    vendorOrderNo: "GSH-992144810",
    clientName: "Mlle Sophie Manga",
    clientPhone: "+237 696 44 22 11",
    clientDestination: "Yaoundé, Bastos face Ambassade Belgique",
    villeFinale: "Yaoundé",
    hubTransit: "Hub Dubaï Cargo",
    trackingVendor: "DXB-EXP-110294",
    itemsDescription: "Vêtements, sacs à main et chaussures tendance (12 articles)",
    poidsKg: 4.2,
    valeurOrigine: 210,
    monnaie: "EUR",
    fretInternational: 25000,
    douaneTaxes: 12000,
    acheminementFinal: 3000,
    totalClient: 40000,
    paymentStatus: "Solde à livraison",
    logisticStatus: "Arrivé agence locale",
    orderDate: "15/09/2026",
    eta: "Disponible pour retrait (Agence Bastos)",
    notes: "SMS de mise à disposition transmis.",
  },
  {
    id: "ec5",
    orderRef: "ECM-2609-ALB-0115",
    platform: "Alibaba B2B",
    vendorOrderNo: "ALB-77401928",
    clientName: "Quincaillerie Moderne du Nord",
    clientPhone: "+237 671 00 99 88",
    clientDestination: "Garoua, Grand Marché Allée des Outillages",
    villeFinale: "Garoua",
    hubTransit: "Hub Guangzhou Chine",
    trackingVendor: "CN-SEA-991823",
    itemsDescription: "Lot 200 kits outillage tournevis de précision & perceuses",
    poidsKg: 48.0,
    valeurOrigine: 1850,
    monnaie: "USD",
    fretInternational: 145000,
    douaneTaxes: 85000,
    acheminementFinal: 25000,
    totalClient: 255000,
    paymentStatus: "Acompte 50%",
    logisticStatus: "Réceptionné Hub Roissy",
    orderDate: "05/09/2026",
    eta: "08/10/2026",
    notes: "Groupage maritime économique puis convoi routier Garoua.",
  },
  {
    id: "ec6",
    orderRef: "ECM-2609-AMZ-0119",
    platform: "Amazon France",
    vendorOrderNo: "408-3341890-55120",
    clientName: "Ing. Christian Talla",
    clientPhone: "+237 655 77 11 00",
    clientDestination: "Garoua, Quartier Poumpoumre",
    villeFinale: "Garoua",
    hubTransit: "Hub Paris Roissy (CDG)",
    trackingVendor: "FR-COL-1129480",
    itemsDescription: "Batterie solaire portable 1000W + Onduleur hybride",
    poidsKg: 14.5,
    valeurOrigine: 799,
    monnaie: "EUR",
    fretInternational: 65000,
    douaneTaxes: 35000,
    acheminementFinal: 10000,
    totalClient: 110000,
    paymentStatus: "Payé à 100%",
    logisticStatus: "Commandé chez marchand",
    orderDate: "Aujourd'hui · 08:30",
    eta: "05/10/2026",
    notes: "En attente livraison entrepôt Roissy CDG.",
  },
];

export default function CommerceConsole() {
  const [data, setData] = useState<EcommerceOrderItem[]>(SEED_ECOMMERCE_ORDERS);
  const [search, setSearch] = useState("");
  const [platformFilter, setPlatformFilter] = useState("Toutes");
  const [villeFilter, setVilleFilter] = useState("Toutes");
  const [statusFilter, setStatusFilter] = useState("Tous");
  const [view, setView] = useState<"table" | "kanban" | "hubs">("table");

  // Display toggles
  const [isFullScreenPage, setIsFullScreenPage] = useState(false);
  const [isFullScreenTable, setIsFullScreenTable] = useState(false);
  const [showKpiCards, setShowKpiCards] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // Modals
  const [newOrderModal, setNewOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<EcommerceOrderItem | null>(null);
  const [statusUpdateItem, setStatusUpdateItem] = useState<EcommerceOrderItem | null>(null);
  const [whatsappModalItem, setWhatsappModalItem] = useState<EcommerceOrderItem | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  // New order form state
  const [newOrder, setNewOrder] = useState({
    platform: "Amazon France" as EcommerceOrderItem["platform"],
    vendorOrderNo: "",
    clientName: "",
    clientPhone: "",
    clientDestination: "",
    villeFinale: "Garoua" as EcommerceOrderItem["villeFinale"],
    hubTransit: "Hub Paris Roissy (CDG)" as EcommerceOrderItem["hubTransit"],
    itemsDescription: "",
    poidsKg: 1.5,
    valeurOrigine: 150,
    monnaie: "EUR" as "EUR" | "USD",
    paymentStatus: "Payé à 100%" as EcommerceOrderItem["paymentStatus"],
    notes: "",
  });

  const notify = (msg: string) => {
    setFeedback(msg);
    setTimeout(() => setFeedback(null), 3500);
  };

  const filtered = data.filter((item) => {
    if (platformFilter !== "Toutes" && item.platform !== platformFilter) return false;
    if (villeFilter !== "Toutes" && item.villeFinale !== villeFilter) return false;
    if (statusFilter !== "Tous" && item.logisticStatus !== statusFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const match =
        item.orderRef.toLowerCase().includes(q) ||
        item.vendorOrderNo.toLowerCase().includes(q) ||
        item.clientName.toLowerCase().includes(q) ||
        item.clientPhone.includes(q) ||
        item.itemsDescription.toLowerCase().includes(q) ||
        item.clientDestination.toLowerCase().includes(q);
      if (!match) return false;
    }
    return true;
  });

  // KPIs
  const totalCommandes = data.length;
  const enTransitHubs = data.filter(
    (d) => d.logisticStatus === "Réceptionné Hub Roissy" || d.logisticStatus === "En vol / fret maritime"
  ).length;
  const enDouane = data.filter((d) => d.logisticStatus === "Dédouanement Cameroun").length;
  const acheminementNord = data.filter((d) => d.villeFinale === "Garoua" && d.logisticStatus !== "Livré au client").length;
  const totalFretRevenu = data.reduce((acc, curr) => acc + toFloat(curr.totalClient), 0);

  const handleUpdateStatus = (newStatus: EcommerceOrderItem["logisticStatus"]) => {
    if (!statusUpdateItem) return;
    setData((prev) =>
      prev.map((d) => (d.id === statusUpdateItem.id ? { ...d, logisticStatus: newStatus } : d))
    );
    notify(`Statut logistique mis à jour : ${newStatus}`);
    setStatusUpdateItem(null);
  };

  const handleCreateOrder = (e: React.FormEvent) => {
    e.preventDefault();
    // Estimation fret: 10 000 F / kg + douane ~ 20%
    const poids = toFloat(newOrder.poidsKg, 1);
    const fret = poids * 12000;
    const douane = toFloat(newOrder.valeurOrigine, 0) * 650 * 0.18;
    const acheminement = newOrder.villeFinale === "Garoua" ? 6000 : 2500;
    const total = fret + douane + acheminement;

    const item: EcommerceOrderItem = {
      id: `ec${Date.now()}`,
      orderRef: `ECM-2609-${newOrder.platform.slice(0, 3).toUpperCase()}-00${data.length + 1}`,
      platform: newOrder.platform,
      vendorOrderNo: newOrder.vendorOrderNo || "COMM-MARCHAND-AUTO",
      clientName: newOrder.clientName || "Client E-commerce",
      clientPhone: newOrder.clientPhone || "+237 600 00 00 00",
      clientDestination: newOrder.clientDestination || `${newOrder.villeFinale} Centre`,
      villeFinale: newOrder.villeFinale,
      hubTransit: newOrder.hubTransit,
      trackingVendor: `TRK-${Math.floor(100000 + Math.random() * 900000)}`,
      itemsDescription: newOrder.itemsDescription || "Colis commande en ligne",
      poidsKg: poids,
      valeurOrigine: Number(newOrder.valeurOrigine) || 100,
      monnaie: newOrder.monnaie,
      fretInternational: fret,
      douaneTaxes: douane,
      acheminementFinal: acheminement,
      totalClient: total,
      paymentStatus: newOrder.paymentStatus,
      logisticStatus: "Commandé chez marchand",
      orderDate: "Aujourd'hui · Récent",
      eta: "Dans 7 à 10 jours",
      notes: newOrder.notes,
    };

    setData([item, ...data]);
    notify(`Commande e-commerce enregistrée avec succès : ${item.orderRef}`);
    setNewOrderModal(false);
  };

  const kanbanLanes: { title: string; status: EcommerceOrderItem["logisticStatus"]; badge: string }[] = [
    { title: "Commandé chez marchand", status: "Commandé chez marchand", badge: "bg-gray-100 text-gray-800" },
    { title: "Reçu Hub Europe / Chine", status: "Réceptionné Hub Roissy", badge: "bg-blue-100 text-blue-800" },
    { title: "En vol international", status: "En vol / fret maritime", badge: "bg-indigo-100 text-indigo-800" },
    { title: "Dédouanement aéroport", status: "Dédouanement Cameroun", badge: "bg-amber-100 text-amber-800" },
    { title: "Acheminement Garoua/Région", status: "Acheminement Garoua", badge: "bg-purple-100 text-purple-800" },
    { title: "Prêt agence / Livré", status: "Arrivé agence locale", badge: "bg-emerald-100 text-emerald-800" },
  ];

  return (
    <div
      className={classNames(
        "h-full flex flex-col bg-[#faf7ff] text-[#1f1b2e] overflow-hidden transition-all",
        isFullScreenPage ? "fixed inset-0 z-50 p-6 bg-[#faf7ff]" : "p-6 lg:p-7"
      )}
    >
      {/* Toast Alert */}
      {feedback && (
        <div className="fixed top-14 right-6 z-50 bg-[#1f1b2e] text-white px-4 py-2.5 rounded-xl shadow-xl flex items-center gap-2.5 text-xs animate-fade-in border border-purple-400/30">
          <CheckCircleIcon className="w-4 h-4 text-emerald-400 shrink-0" />
          <span>{feedback}</span>
        </div>
      )}

      {/* Header bar */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4 shrink-0">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-purple-600 to-pink-500 text-white flex items-center justify-center shadow-xs">
              <ShoppingCartIcon className="w-5 h-5" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-[#1f1b2e]">
              Commerce & Commandes en Ligne (Dropshipping / Import)
            </h1>

            {/* Plein écran Page */}
            <button
              type="button"
              onClick={() => setIsFullScreenPage(!isFullScreenPage)}
              title={isFullScreenPage ? "Quitter le plein écran" : "Plein écran sur toute la page"}
              className="p-1.5 rounded-lg border border-purple-200 bg-white hover:bg-purple-50 text-purple-800 text-xs font-semibold flex items-center gap-1 shadow-2xs cursor-pointer"
            >
              {isFullScreenPage ? (
                <>
                  <ArrowsPointingInIcon className="w-3.5 h-3.5" />
                  <span>Réduire</span>
                </>
              ) : (
                <>
                  <ArrowsPointingOutIcon className="w-3.5 h-3.5" />
                  <span>Plein écran</span>
                </>
              )}
            </button>

            {/* Masquer / Afficher stats */}
            <button
              type="button"
              onClick={() => setShowKpiCards(!showKpiCards)}
              title={showKpiCards ? "Masquer les statistiques" : "Afficher les statistiques"}
              className="p-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-xs font-semibold flex items-center gap-1 shadow-2xs cursor-pointer"
            >
              {showKpiCards ? (
                <>
                  <EyeSlashIcon className="w-3.5 h-3.5" />
                  <span>Masquer stats</span>
                </>
              ) : (
                <>
                  <EyeIcon className="w-3.5 h-3.5" />
                  <span>Afficher stats</span>
                </>
              )}
            </button>
          </div>
          <p className="text-xs text-[#6b6480] mt-1 font-medium">
            Achats Amazon, AliExpress, Shein & Europe expédiés vers Garoua, Douala, Yaoundé · Hubs Paris CDG, Dubaï, Chine & dédouanement
          </p>
        </div>

        {/* Action Buttons Top */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => notify(`Export commandes e-commerce généré (${filtered.length} lignes)`)}
            className="flex items-center gap-1.5 h-9 px-3 border border-slate-300 bg-white hover:bg-slate-50 rounded-lg text-xs font-semibold text-slate-700 shadow-xs cursor-pointer"
          >
            <span className="w-2 h-2 rounded-xs bg-emerald-600" />
            <span>Export Excel</span>
          </button>
          <button
            type="button"
            onClick={() => setNewOrderModal(true)}
            className="flex items-center gap-1.5 h-9 px-3.5 bg-gradient-to-r from-purple-700 to-indigo-700 hover:from-purple-800 hover:to-indigo-800 text-white rounded-lg text-xs font-bold shadow-xs cursor-pointer transition-transform hover:scale-102"
          >
            <PlusIcon className="w-4 h-4 stroke-2" />
            <span>Importer commande en ligne</span>
          </button>
        </div>
      </div>

      {/* KPI Cards (Collapsible) */}
      {showKpiCards && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3.5 shrink-0 animate-fade-in">
          <div className="p-3 bg-white border border-[#ece7f8] rounded-xl shadow-xs">
            <div className="text-[10px] font-bold tracking-wider uppercase text-[#8b81a8]">
              Commandes E-Commerce
            </div>
            <div className="text-xl font-bold text-purple-700 mt-1">{totalCommandes}</div>
            <div className="text-[11px] text-[#9b93b3]">Amazon, Shein, AliExpress</div>
          </div>
          <div className="p-3 bg-white border border-[#ece7f8] rounded-xl shadow-xs">
            <div className="text-[10px] font-bold tracking-wider uppercase text-[#8b81a8]">
              En Hubs Internationaux
            </div>
            <div className="text-xl font-bold text-blue-700 mt-1">{enTransitHubs}</div>
            <div className="text-[11px] text-[#9b93b3]">Roissy CDG / Chine / Dubaï</div>
          </div>
          <div className="p-3 bg-white border border-[#ece7f8] rounded-xl shadow-xs">
            <div className="text-[10px] font-bold tracking-wider uppercase text-[#8b81a8]">
              En Douane Cameroun
            </div>
            <div className="text-xl font-bold text-amber-700 mt-1">{enDouane}</div>
            <div className="text-[11px] text-[#9b93b3]">dédouanement aéroport</div>
          </div>
          <div className="p-3 bg-white border border-[#ece7f8] rounded-xl shadow-xs">
            <div className="text-[10px] font-bold tracking-wider uppercase text-[#8b81a8]">
              Destination Garoua & Nord
            </div>
            <div className="text-xl font-bold text-indigo-700 mt-1">{acheminementNord}</div>
            <div className="text-[11px] text-[#9b93b3]">colis acheminés vers le Nord</div>
          </div>
          <div className="p-3 bg-white border border-[#ece7f8] rounded-xl shadow-xs">
            <div className="text-[10px] font-bold tracking-wider uppercase text-[#8b81a8]">
              Frais & Prestations
            </div>
            <div className="text-xl font-bold text-emerald-700 mt-1">
              {money(totalFretRevenu)}
            </div>
            <div className="text-[11px] text-[#9b93b3]">fret + dédouanement total</div>
          </div>
        </div>
      )}

      {/* Control Bar: View Switcher, Search, Filter Toggle */}
      <div className="flex items-center gap-3 flex-wrap mb-3 shrink-0">
        <div className="flex p-0.5 bg-[#f1ecfe] rounded-lg gap-1">
          <button
            type="button"
            onClick={() => setView("table")}
            className={classNames(
              "px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer",
              view === "table" ? "bg-white text-purple-900 shadow-xs" : "text-[#7c729b] hover:text-purple-900"
            )}
          >
            Tableau des commandes
          </button>
          <button
            type="button"
            onClick={() => setView("kanban")}
            className={classNames(
              "px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer",
              view === "kanban" ? "bg-white text-purple-900 shadow-xs" : "text-[#7c729b] hover:text-purple-900"
            )}
          >
            Pipeline logistique
          </button>
          <button
            type="button"
            onClick={() => setView("hubs")}
            className={classNames(
              "px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer",
              view === "hubs" ? "bg-white text-purple-900 shadow-xs" : "text-[#7c729b] hover:text-purple-900"
            )}
          >
            Hubs Internationaux
          </button>
        </div>

        {/* Search input */}
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Réf commande, n° Amazon/AliExpress, client, article, Garoua..."
          className="flex-1 min-w-[260px] h-9 px-3.5 border border-purple-200/90 rounded-lg bg-white text-xs text-[#1f1b2e] focus:outline-none focus:ring-2 focus:ring-purple-400"
        />

        {/* Quick Plateforme Filter */}
        <div className="flex items-center gap-1 text-xs">
          {["Toutes", "Amazon France", "AliExpress", "Shein"].map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPlatformFilter(p)}
              className={classNames(
                "h-8 px-2.5 rounded-lg border text-xs font-semibold transition-colors cursor-pointer",
                platformFilter === p
                  ? "bg-purple-700 text-white border-purple-700 shadow-xs"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-purple-50"
              )}
            >
              {p}
            </button>
          ))}
        </div>

        {/* Toggle Filters panel */}
        <button
          type="button"
          onClick={() => setShowFilters(!showFilters)}
          className="h-9 px-3.5 border border-purple-200 bg-purple-50 hover:bg-purple-100 rounded-lg text-xs font-semibold text-purple-900 transition-colors cursor-pointer flex items-center gap-1.5"
        >
          <FunnelIcon className="w-3.5 h-3.5" />
          <span>{showFilters ? "Masquer filtres" : "Plus de filtres"}</span>
        </button>
      </div>

      {/* Expanded Filters Panel */}
      {showFilters && (
        <div className="p-3.5 bg-white border border-[#ede9fe] rounded-xl mb-3 shrink-0 shadow-xs animate-fade-in">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div>
              <span className="block text-[10px] font-bold text-[#8b81a8] uppercase mb-1">Destination finale</span>
              <select
                value={villeFilter}
                onChange={(e) => setVilleFilter(e.target.value)}
                className="w-full h-8 px-2 border border-gray-200 rounded-md text-xs bg-white"
              >
                <option value="Toutes">Toutes les destinations</option>
                <option value="Garoua">Garoua (Nord)</option>
                <option value="Douala">Douala</option>
                <option value="Yaoundé">Yaoundé</option>
                <option value="Maroua">Maroua</option>
              </select>
            </div>
            <div>
              <span className="block text-[10px] font-bold text-[#8b81a8] uppercase mb-1">Étape Logistique</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full h-8 px-2 border border-gray-200 rounded-md text-xs bg-white"
              >
                <option value="Tous">Toutes les étapes</option>
                <option value="Commandé chez marchand">Commandé chez marchand</option>
                <option value="Réceptionné Hub Roissy">Réceptionné Hub Roissy</option>
                <option value="En vol / fret maritime">En vol international</option>
                <option value="Dédouanement Cameroun">Dédouanement aéroport</option>
                <option value="Acheminement Garoua">Acheminement Garoua</option>
                <option value="Arrivé agence locale">Arrivé agence locale</option>
              </select>
            </div>
            <div>
              <span className="block text-[10px] font-bold text-[#8b81a8] uppercase mb-1">Plateforme marchande</span>
              <select
                value={platformFilter}
                onChange={(e) => setPlatformFilter(e.target.value)}
                className="w-full h-8 px-2 border border-gray-200 rounded-md text-xs bg-white"
              >
                <option value="Toutes">Toutes les plateformes</option>
                <option value="Amazon France">Amazon France</option>
                <option value="AliExpress">AliExpress</option>
                <option value="Shein">Shein</option>
                <option value="Alibaba B2B">Alibaba B2B</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={() => {
                  setPlatformFilter("Toutes");
                  setVilleFilter("Toutes");
                  setStatusFilter("Tous");
                  setSearch("");
                }}
                className="w-full h-8 px-3 border border-gray-200 hover:bg-gray-100 rounded-md text-xs font-semibold text-gray-700"
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div
        className={classNames(
          "flex-1 min-h-0 overflow-hidden flex flex-col bg-white border border-[#ece7f8] rounded-xl shadow-xs transition-all",
          isFullScreenTable ? "fixed inset-0 z-50 p-6 bg-[#faf7ff]" : ""
        )}
      >
        {/* Table / View Sub-Header */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-[#fbf9ff] border-b border-[#ece7f8] text-xs shrink-0">
          <div className="flex items-center gap-2">
            <span className="font-bold text-[#1f1b2e]">Commandes Import & Dropshipping</span>
            <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full font-bold text-[11px]">
              {filtered.length} commandes
            </span>
            <span className="text-[11px] text-gray-500 hidden sm:inline">
              (Cliquez pour voir le détail ou notifier le client par WhatsApp)
            </span>
          </div>

          {/* Fullscreen Table Toggle */}
          <button
            type="button"
            onClick={() => setIsFullScreenTable(!isFullScreenTable)}
            className="p-1 rounded-md border border-gray-200 bg-white hover:bg-gray-100 text-gray-700 text-xs font-semibold flex items-center gap-1 cursor-pointer"
            title={isFullScreenTable ? "Réduire le tableau" : "Agrandir le tableau en plein écran"}
          >
            {isFullScreenTable ? (
              <>
                <ArrowsPointingInIcon className="w-3.5 h-3.5" />
                <span>Plein écran tableau (fermer)</span>
              </>
            ) : (
              <>
                <ArrowsPointingOutIcon className="w-3.5 h-3.5" />
                <span>Plein écran tableau</span>
              </>
            )}
          </button>
        </div>

        {/* View 1: Table View */}
        {view === "table" && (
          <div className="flex-1 overflow-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead className="bg-[#fbf9ff] sticky top-0 z-10 border-b border-[#ece7f8] text-[#7c729b] font-semibold text-[11px]">
                <tr>
                  <th className="py-2.5 px-3">Réf / Date</th>
                  <th className="py-2.5 px-3">Plateforme & N° Marchand</th>
                  <th className="py-2.5 px-3">Article & Poids</th>
                  <th className="py-2.5 px-3">Client & Destination</th>
                  <th className="py-2.5 px-3">Hub & Transit</th>
                  <th className="py-2.5 px-3">Fret & Douane</th>
                  <th className="py-2.5 px-3">Statut Logistique</th>
                  <th className="py-2.5 px-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-gray-800">
                {filtered.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => setSelectedOrder(item)}
                    className="hover:bg-purple-50/50 cursor-pointer transition-colors"
                  >
                    <td className="py-2.5 px-3 font-semibold text-purple-950 whitespace-nowrap">
                      <div>{item.orderRef}</div>
                      <div className="text-[10px] text-gray-400 font-normal">{item.orderDate}</div>
                    </td>
                    <td className="py-2.5 px-3 whitespace-nowrap">
                      <span className="font-bold text-gray-900">{item.platform}</span>
                      <div className="text-[10px] text-gray-500 font-mono">{item.vendorOrderNo}</div>
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="font-medium text-gray-900 truncate max-w-[200px]" title={item.itemsDescription}>
                        {item.itemsDescription}
                      </div>
                      <div className="text-[10px] text-gray-500 font-semibold">
                        {item.poidsKg} kg · {item.valeurOrigine} {item.monnaie}
                      </div>
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="font-semibold text-gray-900">{item.clientName}</div>
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-purple-700">
                        <MapPinIcon className="w-3 h-3 text-purple-500" />
                        {item.villeFinale}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 whitespace-nowrap">
                      <div className="text-gray-900 font-medium">{item.hubTransit}</div>
                      <div className="text-[10px] text-blue-600 font-mono">{item.trackingVendor}</div>
                    </td>
                    <td className="py-2.5 px-3 whitespace-nowrap">
                      <div className="font-bold text-emerald-700">{money(item.totalClient)}</div>
                      <div className="text-[10px] text-gray-400">
                        Fret {money(item.fretInternational)} · Douane {money(item.douaneTaxes)}
                      </div>
                    </td>
                    <td className="py-2.5 px-3 whitespace-nowrap">
                      <span
                        className={classNames(
                          "px-2 py-0.5 rounded-full text-[10px] font-bold inline-block",
                          item.logisticStatus === "Arrivé agence locale" || item.logisticStatus === "Livré au client"
                            ? "bg-emerald-100 text-emerald-800"
                            : item.logisticStatus === "Acheminement Garoua"
                            ? "bg-purple-100 text-purple-800"
                            : item.logisticStatus === "Dédouanement Cameroun"
                            ? "bg-amber-100 text-amber-800"
                            : item.logisticStatus === "En vol / fret maritime"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-700"
                        )}
                      >
                        {item.logisticStatus}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setWhatsappModalItem(item);
                          }}
                          title="Avis WhatsApp Client"
                          className="px-2 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-bold text-[10px] rounded border border-emerald-200 flex items-center gap-1"
                        >
                          <ChatBubbleLeftRightIcon className="w-3 h-3 text-emerald-600" />
                          <span>WhatsApp</span>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setStatusUpdateItem(item);
                          }}
                          className="px-2 py-1 bg-purple-100 hover:bg-purple-200 text-purple-800 font-semibold text-[10px] rounded"
                        >
                          Étape
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* View 2: Pipeline Kanban */}
        {view === "kanban" && (
          <div className="flex-1 overflow-x-auto p-4 bg-[#fbf9ff]">
            <div className="flex gap-3.5 min-w-[1100px] h-full">
              {kanbanLanes.map((lane) => {
                const laneOrders = filtered.filter((d) => d.logisticStatus === lane.status);
                return (
                  <div
                    key={lane.status}
                    className="flex-1 flex flex-col rounded-xl border border-gray-200 bg-white/70 p-3 min-w-[210px] max-w-[260px]"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-bold text-xs text-gray-800 truncate" title={lane.title}>
                        {lane.title}
                      </span>
                      <span className={classNames("px-2 py-0.5 rounded-full text-[10px] font-bold", lane.badge)}>
                        {laneOrders.length}
                      </span>
                    </div>

                    <div className="flex-1 overflow-y-auto space-y-2.5 pr-1">
                      {laneOrders.map((item) => (
                        <div
                          key={item.id}
                          onClick={() => setSelectedOrder(item)}
                          className="p-3 bg-white rounded-lg border border-gray-200 shadow-2xs hover:shadow-md transition-shadow cursor-pointer"
                        >
                          <div className="flex items-center justify-between text-[11px] mb-1">
                            <span className="font-bold text-purple-900">{item.platform}</span>
                            <span className="text-gray-400 text-[10px]">{item.poidsKg} kg</span>
                          </div>
                          <div className="text-xs font-bold text-gray-900 truncate">{item.clientName}</div>
                          <div className="text-[11px] text-gray-500 truncate mt-0.5">{item.itemsDescription}</div>
                          <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between text-[10px]">
                            <span className="font-bold text-purple-700">{item.villeFinale}</span>
                            <span className="font-semibold text-emerald-700">{money(item.totalClient)}</span>
                          </div>
                        </div>
                      ))}
                      {laneOrders.length === 0 && (
                        <div className="h-20 flex items-center justify-center text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg">
                          Aucun colis
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* View 3: Hubs Internationaux Overview */}
        {view === "hubs" && (
          <div className="flex-1 overflow-auto p-5 bg-[#fbf9ff]">
            <h2 className="text-sm font-bold text-gray-900 mb-3">Réseau des Hubs de Réception & Transit E-Commerce</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { name: "Hub Paris Roissy (CDG)", country: "France / Europe", platform: "Amazon FR, Zara, sites EU", packages: 4, manager: "Fabrice Lefevre" },
                { name: "Hub Guangzhou Chine", country: "Chine / Asie", platform: "AliExpress, Shein, Alibaba", packages: 2, manager: "Chen Wei" },
                { name: "Hub Dubaï Cargo", country: "Émirats Arabes Unis", platform: "Électronique, Shein Express", packages: 1, manager: "Tariq Mansoor" },
                { name: "Hub Douala Aéroport", country: "Cameroun (Entrée)", platform: "Tri & Dispatch Garoua/Ydé", packages: 5, manager: "Sandrine Kotto" },
              ].map((hub, idx) => (
                <div key={idx} className="p-4 bg-white border border-[#ece7f8] rounded-xl shadow-xs">
                  <div className="flex items-center gap-2 mb-2">
                    <GlobeAltIcon className="w-5 h-5 text-purple-600" />
                    <h3 className="font-bold text-gray-900 text-sm">{hub.name}</h3>
                  </div>
                  <div className="text-xs text-gray-500">{hub.country}</div>
                  <div className="text-xs text-purple-700 font-semibold mt-1">Dédié à : {hub.platform}</div>
                  <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between items-center text-xs">
                    <span className="text-gray-500">Colis en transit :</span>
                    <strong className="text-purple-900 font-bold text-sm">{hub.packages}</strong>
                  </div>
                  <button
                    type="button"
                    onClick={() => notify(`Manifeste d'expédition généré pour le ${hub.name}`)}
                    className="mt-3 w-full py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-900 font-semibold text-xs rounded-lg cursor-pointer"
                  >
                    Voir manifeste Hub
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modal: Update Logistic Status */}
      {statusUpdateItem && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl border border-gray-200 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-900">Étape Logistique - {statusUpdateItem.orderRef}</h3>
              <button type="button" onClick={() => setStatusUpdateItem(null)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-gray-600 mb-3">
              Mettre à jour l'acheminement vers <strong>{statusUpdateItem.villeFinale}</strong> :
            </p>
            <div className="space-y-2 mb-4">
              {[
                "Commandé chez marchand",
                "Réceptionné Hub Roissy",
                "En vol / fret maritime",
                "Dédouanement Cameroun",
                "Acheminement Garoua",
                "Arrivé agence locale",
                "Livré au client",
              ].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => handleUpdateStatus(s as any)}
                  className={classNames(
                    "w-full py-2 px-3 text-left text-xs font-semibold rounded-lg border transition-colors",
                    statusUpdateItem.logisticStatus === s
                      ? "bg-purple-700 text-white border-purple-700"
                      : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-purple-50"
                  )}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal: WhatsApp Notification Template */}
      {whatsappModalItem && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl border border-gray-200 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ChatBubbleLeftRightIcon className="w-5 h-5 text-emerald-600" />
                <h3 className="font-bold text-gray-900">Notification Client WhatsApp</h3>
              </div>
              <button type="button" onClick={() => setWhatsappModalItem(null)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-gray-600 mb-3">
              Message prêt à être envoyé à <strong>{whatsappModalItem.clientName}</strong> ({whatsappModalItem.clientPhone}) :
            </p>

            <div className="p-3 bg-emerald-50/70 border border-emerald-200 rounded-xl text-xs text-emerald-950 font-mono mb-4 leading-relaxed whitespace-pre-wrap">
              {`Bonjour ${whatsappModalItem.clientName},\n\nVotre commande ${whatsappModalItem.platform} (${whatsappModalItem.itemsDescription}) référence ${whatsappModalItem.orderRef} est actuellement en statut : *${whatsappModalItem.logisticStatus}*.\n\n- Destination : ${whatsappModalItem.clientDestination}\n- Poids : ${whatsappModalItem.poidsKg} kg\n- Estimation d'arrivée : ${whatsappModalItem.eta}\n\nMerci de votre confiance !\nL'équipe Logistique & Expéditions.`}
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setWhatsappModalItem(null)}
                className="flex-1 py-2 border border-gray-300 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-50"
              >
                Fermer
              </button>
              <button
                type="button"
                onClick={() => {
                  notify(`Message WhatsApp transmis au client ${whatsappModalItem.clientName} (${whatsappModalItem.clientPhone})`);
                  setWhatsappModalItem(null);
                }}
                className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow-sm flex items-center justify-center gap-1.5"
              >
                <span>Envoyer au client</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Details Commande */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl border border-gray-200 animate-fade-in max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-4">
              <div>
                <h3 className="font-bold text-gray-900 text-base">{selectedOrder.orderRef}</h3>
                <span className="text-xs text-purple-700 font-semibold">{selectedOrder.platform} · {selectedOrder.vendorOrderNo}</span>
              </div>
              <button type="button" onClick={() => setSelectedOrder(null)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3.5 text-xs">
              <div className="p-3 bg-purple-50/70 rounded-xl">
                <div className="text-[10px] font-bold text-purple-900 uppercase">Article & Description Commande</div>
                <div className="font-bold text-gray-900 mt-1 text-sm">{selectedOrder.itemsDescription}</div>
                <div className="text-gray-600 mt-0.5">
                  Valeur marchandise : <strong>{selectedOrder.valeurOrigine} {selectedOrder.monnaie}</strong> · Poids réel : <strong>{selectedOrder.poidsKg} kg</strong>
                </div>
              </div>

              <div className="p-3 bg-blue-50/70 rounded-xl">
                <div className="text-[10px] font-bold text-blue-900 uppercase">Client Final au Cameroun</div>
                <div className="font-bold text-gray-900 mt-1">{selectedOrder.clientName}</div>
                <div className="text-gray-600">{selectedOrder.clientDestination}</div>
                <div className="text-blue-700 mt-0.5 font-semibold">{selectedOrder.clientPhone}</div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 border border-gray-200 rounded-xl">
                  <div className="text-[10px] text-gray-500 uppercase font-semibold">Hub Transit</div>
                  <div className="font-bold text-gray-900 mt-1">{selectedOrder.hubTransit}</div>
                  <div className="text-blue-600 font-mono text-[10px]">{selectedOrder.trackingVendor}</div>
                </div>

                <div className="p-3 border border-gray-200 rounded-xl">
                  <div className="text-[10px] text-gray-500 uppercase font-semibold">Décomposition Frais</div>
                  <div className="text-gray-700">Fret : {money(selectedOrder.fretInternational)}</div>
                  <div className="text-gray-700">Douane : {money(selectedOrder.douaneTaxes)}</div>
                  <div className="text-gray-700">Achem. {selectedOrder.villeFinale} : {money(selectedOrder.acheminementFinal)}</div>
                  <div className="font-bold text-emerald-700 mt-1 pt-1 border-t border-gray-100">
                    Total : {money(selectedOrder.totalClient)}
                  </div>
                </div>
              </div>

              {selectedOrder.notes && (
                <div className="p-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-600 text-xs">
                  <strong>Notes :</strong> {selectedOrder.notes}
                </div>
              )}
            </div>

            <div className="mt-5 flex gap-2 pt-3 border-t border-gray-100">
              <button
                type="button"
                onClick={() => notify(`Facture proforma émise pour ${selectedOrder.orderRef}`)}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-50"
              >
                Facture Proforma
              </button>
              <button
                type="button"
                onClick={() => {
                  setWhatsappModalItem(selectedOrder);
                  setSelectedOrder(null);
                }}
                className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold"
              >
                Avis WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Importer / Créer Commande E-commerce */}
      {newOrderModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl border border-gray-200 animate-fade-in max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-4">
              <h3 className="font-bold text-gray-900 text-base">Importer Commande E-commerce / Dropshipping</h3>
              <button type="button" onClick={() => setNewOrderModal(false)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateOrder} className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Plateforme source</label>
                  <select
                    value={newOrder.platform}
                    onChange={(e) => setNewOrder({ ...newOrder, platform: e.target.value as any })}
                    className="w-full h-8 px-2.5 border border-gray-200 rounded-lg bg-white"
                  >
                    <option value="Amazon France">Amazon France</option>
                    <option value="Amazon USA">Amazon USA</option>
                    <option value="AliExpress">AliExpress</option>
                    <option value="Shein">Shein</option>
                    <option value="Alibaba B2B">Alibaba B2B</option>
                    <option value="Zara / Fashion">Zara / Fashion</option>
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-gray-700 mb-1">N° Commande Marchand (Réf)</label>
                  <input
                    type="text"
                    required
                    placeholder="ex: 408-7291823-19283"
                    value={newOrder.vendorOrderNo}
                    onChange={(e) => setNewOrder({ ...newOrder, vendorOrderNo: e.target.value })}
                    className="w-full h-8 px-2.5 border border-gray-200 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-1">Désignation du ou des articles</label>
                <input
                  type="text"
                  required
                  placeholder="ex: PC Portable Asus Zenbook + sacoche"
                  value={newOrder.itemsDescription}
                  onChange={(e) => setNewOrder({ ...newOrder, itemsDescription: e.target.value })}
                  className="w-full h-8 px-2.5 border border-gray-200 rounded-lg"
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Poids estimé (Kg)</label>
                  <input
                    type="number"
                    step="0.1"
                    required
                    value={newOrder.poidsKg}
                    onChange={(e) => setNewOrder({ ...newOrder, poidsKg: Number(e.target.value) })}
                    className="w-full h-8 px-2.5 border border-gray-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Prix article</label>
                  <input
                    type="number"
                    required
                    value={newOrder.valeurOrigine}
                    onChange={(e) => setNewOrder({ ...newOrder, valeurOrigine: Number(e.target.value) })}
                    className="w-full h-8 px-2.5 border border-gray-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Devise</label>
                  <select
                    value={newOrder.monnaie}
                    onChange={(e) => setNewOrder({ ...newOrder, monnaie: e.target.value as any })}
                    className="w-full h-8 px-2 border border-gray-200 rounded-lg bg-white"
                  >
                    <option value="EUR">Euros (€)</option>
                    <option value="USD">Dollars ($)</option>
                  </select>
                </div>
              </div>

              <div className="p-3 bg-purple-50/50 rounded-xl space-y-2">
                <span className="font-bold text-purple-900 block text-[11px]">Client & Destination Cameroun</span>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    required
                    placeholder="Nom du client"
                    value={newOrder.clientName}
                    onChange={(e) => setNewOrder({ ...newOrder, clientName: e.target.value })}
                    className="h-8 px-2.5 border border-gray-200 rounded-lg bg-white"
                  />
                  <input
                    type="text"
                    required
                    placeholder="Téléphone (+237...)"
                    value={newOrder.clientPhone}
                    onChange={(e) => setNewOrder({ ...newOrder, clientPhone: e.target.value })}
                    className="h-8 px-2.5 border border-gray-200 rounded-lg bg-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={newOrder.villeFinale}
                    onChange={(e) => setNewOrder({ ...newOrder, villeFinale: e.target.value as any })}
                    className="h-8 px-2 border border-gray-200 rounded-lg bg-white"
                  >
                    <option value="Garoua">Garoua (Nord)</option>
                    <option value="Douala">Douala</option>
                    <option value="Yaoundé">Yaoundé</option>
                    <option value="Maroua">Maroua</option>
                  </select>
                  <input
                    type="text"
                    required
                    placeholder="Quartier / Adresse précise"
                    value={newOrder.clientDestination}
                    onChange={(e) => setNewOrder({ ...newOrder, clientDestination: e.target.value })}
                    className="h-8 px-2.5 border border-gray-200 rounded-lg bg-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Hub de réception</label>
                  <select
                    value={newOrder.hubTransit}
                    onChange={(e) => setNewOrder({ ...newOrder, hubTransit: e.target.value as any })}
                    className="w-full h-8 px-2 border border-gray-200 rounded-lg bg-white"
                  >
                    <option value="Hub Paris Roissy (CDG)">Hub Paris Roissy (CDG)</option>
                    <option value="Hub Dubaï Cargo">Hub Dubaï Cargo</option>
                    <option value="Hub Guangzhou Chine">Hub Guangzhou Chine</option>
                    <option value="Hub Bruxelles">Hub Bruxelles</option>
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Paiement</label>
                  <select
                    value={newOrder.paymentStatus}
                    onChange={(e) => setNewOrder({ ...newOrder, paymentStatus: e.target.value as any })}
                    className="w-full h-8 px-2 border border-gray-200 rounded-lg bg-white"
                  >
                    <option value="Payé à 100%">Payé à 100%</option>
                    <option value="Acompte 50%">Acompte 50%</option>
                    <option value="Solde à livraison">Solde à livraison</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-1">Remarques ou instructions de livraison</label>
                <input
                  type="text"
                  placeholder="ex: Contacter le client dès arrivée à l'agence Garoua..."
                  value={newOrder.notes}
                  onChange={(e) => setNewOrder({ ...newOrder, notes: e.target.value })}
                  className="w-full h-8 px-2.5 border border-gray-200 rounded-lg"
                />
              </div>

              <div className="pt-3 border-t border-gray-100 flex gap-2">
                <button
                  type="button"
                  onClick={() => setNewOrderModal(false)}
                  className="flex-1 py-2 border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 bg-gradient-to-r from-purple-700 to-indigo-700 hover:from-purple-800 hover:to-indigo-800 text-white rounded-lg text-xs font-bold shadow-sm"
                >
                  Enregistrer et calculer fret
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
